// Single source of truth for "is this account excluded from the AFS
// figures" and the sign-normalised fallback figures build. This logic used
// to be reimplemented independently in TrialBalance.jsx, Review.jsx,
// Statements.jsx and Notes.jsx — a known architectural-drift risk
// (kailux-fixes-log.md §4.1). Consolidated here 8 Sep 2026 alongside the
// control-row fix below, so the four pages can no longer drift out of sync
// with each other, or with the backend's aggregate_figures_from_accounts()
// in backend/main.py.
//
// The real, authoritative figures always come from the backend
// (aggregateFigures() in api/kailux.js) or are carried forward via
// location.state. The buildFiguresFromAccounts() fallback here only runs
// when neither is available (e.g. a page reached directly, or state lost on
// refresh) — it must stay behaviourally identical to the backend function.

export const DEBIT_NATURE_CATEGORIES = new Set([
  'Property Plant & Equipment', 'Intangible Assets', 'Investments', 'Deferred Tax Asset',
  'Inventories', 'Trade and Other Receivables', 'Tax Receivables', 'Prepayments',
  'Cash and Cash Equivalents',
  'Cost of Sales', 'Employee Costs', 'Occupancy Costs', 'Communication Expenses',
  'Travel and Motor Expenses', 'General and Admin Expenses', 'Professional Fees',
  'Depreciation and Amortisation', 'Credit Losses', 'Sales and Marketing',
  'Finance Costs', 'Income Tax Expense',
  // Private trusts: a debit equity movement. Folded into Retained Earnings
  // below exactly as aggregate_figures_from_accounts() does on the backend.
  'Distributions to Beneficiaries',
])

// Trial-balance export files commonly carry a TOTAL / DIFFERENCE / suspense
// style row that is a checksum artifact of the source spreadsheet, not a
// real account. Mirrors backend _CONTROL_ROW_NAME_RE in main.py — keep the
// two patterns in sync if either changes. Found 8 Sep 2026: a new firm's
// trial balance had a "DIFFERENCE (Debit - Credit)" row that the AI mapper
// flagged with very low (but nonzero) confidence, which was enough for the
// old confidence<=0 check to let it through and silently inflate General
// and Admin Expenses by ~R7.45bn.
// "difference" alone is too broad — real accounts like "Difference in
// Foreign Exchange Reserve" or "Timing Difference" (a common deferred-tax
// term) legitimately start with/contain that word. Only match when it's
// explicitly a debit/credit balancing-check row.
const CONTROL_ROW_NAME_RE =
  /^(grand\s+)?(sub[\s-]?)?total$|^difference\s*\(?\s*(debit\s*-?\s*credit|dr\s*-?\s*cr)\s*\)?$|^balancing\s+difference$|^(balance\s+check|check\s+figure)$|^suspense(\s+account)?$|^control\s+account$/i

export const isControlRow = (name) => CONTROL_ROW_NAME_RE.test((name || '').trim())

// 29 Sep 2026 — a "Current Year Earnings" line that DUPLICATES the income-
// statement accounts already in the same trial balance. Mirrors backend
// _is_profit_rollup_row() / _CURRENT_YEAR_EARNINGS_NAME_RE / _PL_CATEGORIES in
// main.py — keep the two in sync if either changes.
//
// Found on a real new firm's trial balance: Revenue R130,428 and Operating
// Expenses R13,665 AND a "Current Year Earnings" account of R116,763 (exactly
// their net). The AI mapper put it in Retained Earnings, so the year's profit
// was counted twice. Detected by NAME *and* AMOUNT, never name alone: a post-
// closing trial balance whose only record of the year's profit is such a line
// (no income-statement accounts) is legitimate and must not be touched.
const CURRENT_YEAR_EARNINGS_NAME_RE =
  /^(current|this) years? (earnings|profit|income|surplus)( loss| deficit)?$|^(net )?(profit|income|surplus|earnings)( loss| deficit)? for the (year|period)$|^net (profit|income)( loss)?$|^retained earnings current (year|period)$/

const PL_CATEGORIES = new Set([
  'Revenue', 'Other Income', 'Cost of Sales', 'Employee Costs', 'Occupancy Costs',
  'Communication Expenses', 'Travel and Motor Expenses', 'General and Admin Expenses',
  'Professional Fees', 'Depreciation and Amortisation', 'Credit Losses',
  'Sales and Marketing', 'Finance Costs', 'Income Tax Expense',
])

const normaliseAccountName = (name) =>
  String(name || '').toLowerCase().replace(/['\u2019]/g, '')
    .replace(/[()/\\\-\u2013\u2014,&]+/g, ' ').replace(/\s+/g, ' ').trim()

export const isProfitRollupRow = (acc, accounts, mappings) => {
  if (!acc || !CURRENT_YEAR_EARNINGS_NAME_RE.test(normaliseAccountName(acc.name))) return false
  const code = String(acc.code ?? '')
  const rowNet = parseFloat(acc.credit || 0) - parseFloat(acc.debit || 0)
  if (Math.abs(rowNet) < 0.01) return false
  let plNet = 0, plAccounts = 0
  for (const other of accounts || []) {
    if (String(other.code ?? '') === code) continue
    const cat = ((mappings || {})[other.code]?.ifrs_category || '').trim()
    if (!PL_CATEGORIES.has(cat)) continue
    const d = parseFloat(other.debit || 0), c = parseFloat(other.credit || 0)
    if (d || c) plAccounts += 1
    plNet += c - d
  }
  return plAccounts > 0 && Math.abs(rowNet - plNet) < 1.0
}

// Codes of every roll-up row in the account list — computed once per render
// by callers that need to test many rows.
export const findProfitRollupCodes = (accounts, mappings) =>
  new Set((accounts || []).filter(a => isProfitRollupRow(a, accounts, mappings)).map(a => String(a.code)))

// 9 Sep 2026 — a control row a preparer has deliberately excluded (via the
// "Exclude" flow in TrialBalance.jsx, gated by a balance check against
// /api/trial-balance/check-exclusion — see that endpoint's docstring in
// main.py for why this is only ever offered when the trial balance still
// balances without the row). This is resolved, not blocking: it should
// stop counting as "unmapped" everywhere, but must still never enter
// figures — excluding a row and having it silently keep counting toward a
// real IFRS category would defeat the entire point.
//
// The true, unbypassable gate is the backend re-verifying the balance check
// itself inside aggregate_figures_from_accounts every time figures are
// computed (validate/generate), regardless of what this client-side flag
// claims — so a stale mapping (e.g. edited outside the normal flow) can
// never slip a bad exclusion through. This client-side helper only affects
// what the preparer sees; it is optimistic display logic, not the security
// boundary.
export const isExcludedControlRow = (mapping, name) =>
  isControlRow(name) && !!(mapping && mapping.excluded)

// `name` is optional so existing call sites that only have the mapping
// object keep working unchanged (control-row detection simply no-ops when
// no name is supplied).
export const isUnmappedAccount = (mapping, name) => {
  if (isExcludedControlRow(mapping, name)) return false
  return !mapping || !mapping.ifrs_category || mapping.source === 'unassigned' ||
    !mapping.confidence || mapping.confidence <= 0 || isControlRow(name)
}

export const buildFiguresFromAccounts = (accounts, mappings) => {
  const figures = {}
  // Backend parity: a profit roll-up row never enters figures, whether it has
  // been excluded (resolved) or not (blocked as unmapped) — see
  // aggregate_figures_from_accounts in main.py.
  const rollupCodes = findProfitRollupCodes(accounts, mappings)
  accounts.forEach(acc => {
    const m = mappings[acc.code]
    if (rollupCodes.has(String(acc.code))) return
    // Excluded control rows never enter figures, resolved or not — checked
    // separately from isUnmappedAccount so "resolved" (not blocking) can
    // never be confused with "safe to sum".
    if (isExcludedControlRow(m, acc.name)) return
    if (isUnmappedAccount(m, acc.name)) return
    const cat = m.ifrs_category
    const debit = parseFloat(acc.debit || 0)
    const credit = parseFloat(acc.credit || 0)
    figures[cat] = (figures[cat] || 0) +
      (DEBIT_NATURE_CATEGORIES.has(cat) ? (debit - credit) : (credit - debit))
  })
  // Backend parity: trust distributions reduce retained earnings; the
  // 'Distributions to Beneficiaries' key stays only as the disclosed amount.
  if (figures['Distributions to Beneficiaries']) {
    figures['Retained Earnings'] = (figures['Retained Earnings'] || 0) - figures['Distributions to Beneficiaries']
  }
  return figures
}

// ── Statement is DERIVED from the category, never typed (28 Oct 2026) ───────────────────────────────
// A mapping carries `ifrs_category` (which the figures follow) and `statement` / `display_name` (which the
// preparer sees). They used to be independently editable, and picking a category closed the editor before
// the statement could be changed — so re-categorising an account left the old statement and label behind.
// A real engagement ended up with ten accounts (R14.67m) labelled "Trade and Other Receivables" on the
// Statement of Financial Position but categorised Cost of Sales: reported as an expense, shown as an asset.
// These two functions are the only place the relationship lives; the backend rejects any mapping where the
// fields still disagree (mapping_statement_conflicts in backend/main.py).
export const INCOME_STATEMENT_CATEGORIES = new Set([
  'Revenue', 'Other Income', 'Cost of Sales', 'Employee Costs', 'Occupancy Costs',
  'Communication Expenses', 'Travel and Motor Expenses', 'General and Admin Expenses',
  'Professional Fees', 'Depreciation and Amortisation', 'Credit Losses', 'Sales and Marketing',
  'Finance Costs', 'Income Tax Expense',
])

export const statementForCategory = (category) =>
  INCOME_STATEMENT_CATEGORIES.has(category) ? 'Income Statement' : 'Statement of Financial Position'

// Pure reducer for one manual edit of one account's mapping. Changing the category re-derives the
// statement and replaces the label/reasoning (which described the previous category) so nothing stale remains.
export const applyMappingEdit = (mapping, field, value) => {
  const next = { ...(mapping || {}), [field]: value, confidence: 100, source: 'manual' }
  if (field === 'ifrs_category') {
    next.statement = statementForCategory(value)
    next.display_name = value
    next.reasoning = 'Set manually by the preparer.'
  }
  return next
}

// True when a SAVED mapping still carries a statement that disagrees with its category — left over from before the
// statement was derived (see above). The screen now displays the derived statement, so without this the backend
// could block generation for a conflict the preparer cannot see. Confirming re-saves the mapping via applyMappingEdit.
export const hasStatementConflict = (m) => {
  if (!m || !m.ifrs_category || !m.statement) return false
  const s = String(m.statement).toLowerCase()
  const stored = s.includes('financial position') ? 'Statement of Financial Position'
    : (s.includes('income') || s.includes('profit')) ? 'Income Statement'
    : null                                            // e.g. the retired 'Notes Only': nothing to contradict
  return stored !== null && stored !== statementForCategory(m.ifrs_category)
}
